import { removeNulls } from 'utils/Object.utils';

type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type LedgerListItem = {
  id: string;
  [key: string]: unknown;
};

export type LedgersListState = {
  byId?: Record<string, LedgerListItem>;
  allIds?: string[];
  isLoading?: boolean;
  error?: { code: string; [key: string]: unknown } | null;
  isAdding?: boolean;
  isDeleting?: boolean;
  addDeleteError?: { code: string; [key: string]: unknown } | null;
  [key: string]: unknown;
};

const initialState: LedgersListState = {
  byId: {},
  allIds: [],
};

const ledgersById = (
  stateArg: Record<string, LedgerListItem> | undefined,
  action: ReducerAction
): Record<string, LedgerListItem> => {
  const state = stateArg || initialState.byId;
  switch (action.type) {
    case 'LEDGERS_ADD_NEW': {
      const { ledger } = action;
      return { ...state, [ledger.id]: ledger };
    }
    case 'LEDGERS_DELETE': {
      const { ledgerId } = action;
      return removeNulls({ ...state, [ledgerId]: null }) as Record<
        string,
        LedgerListItem
      >;
    }
    case 'LEDGERS_FINISHED_ADDING': {
      // Re-adds (or adds, if the optimistic LEDGERS_ADD_NEW entry was lost -
      // e.g. wiped by a LEDGERS_LIST_FINISHED_LOADING refresh landing while
      // the Firebase write was still in flight) the newly-created ledger, so
      // a successful add is never left missing from the list until a manual
      // refresh.
      const { ledger } = action;
      if (!ledger) return state;
      return { ...state, [ledger.id]: ledger };
    }
    case 'LEDGERS_FINISHED_ADDING_WITH_ERROR': {
      // Roll back the optimistic LEDGERS_ADD_NEW: the Firebase write failed,
      // so the phantom ledger must not remain visible in the list.
      const { ledgerId } = action.payload || {};
      if (!ledgerId) return state;
      return removeNulls({ ...state, [ledgerId]: null }) as Record<
        string,
        LedgerListItem
      >;
    }
    case 'LEDGERS_FINISHED_DELETING': {
      // Re-removes (or removes, if the optimistic LEDGERS_DELETE removal was
      // undone - e.g. a LEDGERS_LIST_FINISHED_LOADING refresh landing with a
      // stale server snapshot, before the async ledgerMeta_onUpdate Cloud
      // Function has mirrored the delete into /users-ledgers/{uid}, while
      // this delete was still in flight) the deleted ledger, so a successful
      // delete never stays ghosted back into the list until another refresh
      // happens to land after the Cloud Function catches up. Mirrors the
      // LEDGERS_FINISHED_ADDING re-add case above, just removing instead of
      // adding.
      const { ledgerId } = action;
      if (!ledgerId) return state;
      return removeNulls({ ...state, [ledgerId]: null }) as Record<
        string,
        LedgerListItem
      >;
    }
    case 'LEDGERS_FINISHED_DELETING_WITH_ERROR': {
      // Roll back the optimistic LEDGERS_DELETE: the Firebase write failed,
      // so the ledger still exists remotely and must reappear in the list.
      const { ledger } = action.payload || {};
      if (!ledger) return state;
      return { ...state, [ledger.id]: ledger };
    }
    default:
      return state;
  }
};

const allLedgers = (
  stateArg: string[] | undefined,
  action: ReducerAction
): string[] => {
  const state = stateArg || initialState.allIds;
  switch (action.type) {
    case 'LEDGERS_ADD_NEW': {
      const { ledger } = action;
      return state.concat([ledger.id]);
    }
    case 'LEDGERS_DELETE': {
      const { ledgerId } = action;
      return state.filter((id) => {
        return id !== ledgerId;
      });
    }
    case 'LEDGERS_FINISHED_ADDING': {
      // Re-adds the ledger regardless of whether a LEDGERS_LIST_FINISHED_LOADING
      // refresh landed in between the optimistic LEDGERS_ADD_NEW and this
      // confirmation - see the matching case in ledgersById below for why.
      const { ledger } = action;
      if (!ledger || state.includes(ledger.id)) return state;
      return state.concat([ledger.id]);
    }
    case 'LEDGERS_FINISHED_ADDING_WITH_ERROR': {
      const { ledgerId } = action.payload || {};
      if (!ledgerId) return state;
      return state.filter((id) => {
        return id !== ledgerId;
      });
    }
    case 'LEDGERS_FINISHED_DELETING': {
      // Re-removes the ledger id regardless of whether a
      // LEDGERS_LIST_FINISHED_LOADING refresh landed in between the
      // optimistic LEDGERS_DELETE and this confirmation - see the matching
      // case in ledgersById above for why.
      const { ledgerId } = action;
      if (!ledgerId) return state;
      return state.filter((id) => {
        return id !== ledgerId;
      });
    }
    case 'LEDGERS_FINISHED_DELETING_WITH_ERROR': {
      const { ledger } = action.payload || {};
      if (!ledger || state.includes(ledger.id)) return state;
      return state.concat([ledger.id]);
    }
    default:
      return state;
  }
};

const ledgersListReducer = (
  stateArg: LedgersListState | undefined,
  action: ReducerAction
): LedgersListState => {
  const state = stateArg || initialState;
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'LEDGERS_LIST_IS_LOADING': {
      return { ...state, isLoading: true };
    }
    case 'LEDGERS_LIST_FINISHED_LOADING': {
      // Merge rather than replace: action.payload is always exactly
      // { byId, allIds } (see ledgersList.actions.js), so fully replacing
      // state here would wipe isAdding/isDeleting/addDeleteError if a list
      // refresh resolves while an add/delete is still in flight. isLoading
      // and error are cleared explicitly, since a successful load should
      // still supersede a previous loading-error state.
      return {
        ...state,
        ...(action.payload || {}),
        isLoading: false,
        error: null,
      };
    }
    case 'LEDGERS_LIST_FINISHED_LOADING_WITH_ERROR': {
      // Merge rather than replace (mirrors LEDGERS_LIST_FINISHED_LOADING
      // above): fully replacing state here would wipe byId/allIds - the
      // currently-visible list - as well as isAdding/isDeleting/
      // addDeleteError if this refresh failure lands while an add/delete is
      // still in flight. Only isLoading/error are updated; error is set
      // (rather than cleared) since this is the failure case.
      const error = action.payload.error || { code: 'Unknown' };
      return {
        ...state,
        error,
        isLoading: false,
      };
    }
    case 'LEDGERS_LIST_ERROR_DISMISSED': {
      // Clears addDeleteError from the Redux store itself (not just local
      // component UI state) - mirrors USER_ACL_UPDATE_ERROR_DISMISSED in
      // admin.reducer.js. Also matters because ledgersList is persisted to
      // localStorage, so a stale error could otherwise resurface on reload
      // with no way for the user to clear it.
      if (!state.addDeleteError) return state;
      return { ...state, addDeleteError: null };
    }
    case 'LEDGERS_IS_ADDING': {
      return { ...state, isAdding: true, addDeleteError: null };
    }
    case 'LEDGERS_IS_DELETING': {
      return { ...state, isDeleting: true, addDeleteError: null };
    }
    case 'LEDGERS_FINISHED_ADDING': {
      return {
        ...state,
        byId: ledgersById(state.byId, action),
        allIds: allLedgers(state.allIds, action),
        isAdding: false,
      };
    }
    case 'LEDGERS_FINISHED_DELETING': {
      return {
        ...state,
        byId: ledgersById(state.byId, action),
        allIds: allLedgers(state.allIds, action),
        isDeleting: false,
      };
    }
    case 'LEDGERS_FINISHED_ADDING_WITH_ERROR': {
      const error = action.payload.error || { code: 'Unknown' };
      return {
        ...state,
        byId: ledgersById(state.byId, action),
        allIds: allLedgers(state.allIds, action),
        isAdding: false,
        addDeleteError: error,
      };
    }
    case 'LEDGERS_FINISHED_DELETING_WITH_ERROR': {
      const error = action.payload.error || { code: 'Unknown' };
      return {
        ...state,
        byId: ledgersById(state.byId, action),
        allIds: allLedgers(state.allIds, action),
        isDeleting: false,
        addDeleteError: error,
      };
    }
    default: {
      return {
        ...state,
        byId: ledgersById(state.byId, action),
        allIds: allLedgers(state.allIds, action),
      };
    }
  }
};

export default ledgersListReducer;
